import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { useAppBanner } from '@/contexts/appBanner';
import { useAppSelector } from '@/store/hooks';
import { CurrencyHud } from '@/components/hub/CurrencyHud';
import { LevelBadge } from '@/components/hub/LevelBadge';
import { UserHeaderBanner } from '@/components/banner/UserHeaderBanner';
import { BannerAvatar } from '@/components/banner/BannerAvatar';
import { useQlashUser } from '@/hooks/useQlashUser';

/**
 * The app's constant chrome: the player's banner + avatar + name, same place on
 * every screen, above <main>. Tapping the banner navigates to /profile — banner
 * customization itself now lives in Profile → Settings → Appearance.
 *
 * Two slots let a screen speak through it without owning it: the top row for
 * options/menus/title, and an overlay layer over the banner box itself. See
 * `contexts/appBanner.ts`.
 */
export function AppBanner() {
  const { setTopNode, setOverlayNode, setSideNode, setBottomNode } = useAppBanner();
  // Only needed to resolve the SELECTED BANNER (IAP record) — not to print a
  // name. The banner shows the player's picture and their banner, nothing else.
  const { t } = useTranslation('general');
  // Una fonte sola per build: su una build Limulo Laravel non viene interrogato affatto.
  // Il perche' (e la corsa che c'era prima) in `hooks/useQlashUser.ts`.
  const me = useQlashUser();
  const compact = useAppSelector((s) => s.settings.bannerCompact);
  const navigate = useNavigate();

  /**
   * Sul Profilo il banner si stringe un po' per far posto al tasto di modifica.
   *
   * È una riduzione LIEVE (90%), diversa da quella dello scroll (62%): lì serve
   * a recuperare altezza, qui solo a liberare la colonna per un tondo. Passando
   * da un'altra schermata la larghezza si anima, così si vede che il banner ha
   * fatto spazio a qualcosa invece di sembrare semplicemente più piccolo.
   *
   * Lo stato vive nella rotta e non in Redux: è una proprietà di DOVE SI È, non
   * una preferenza, e tenerlo in Redux avrebbe richiesto a ogni schermata di
   * ricordarsi di azzerarlo uscendo.
   */
  const onProfile = useLocation().pathname === '/profile';

  // La colonna a destra del banner esiste in due casi: da compressi ospita il
  // tasto della sezione (Hub), sul Profilo il tasto di modifica del banner.
  const hasSide = compact || onProfile;

  return (
    // relative z-10: the avatar is allowed to spill outside this box — it hangs
    // below the banner's bottom edge — and without a stacking order of its own
    // it would be painted over by <main>'s content.
    // pb-2: il banner finiva a 6px dal bordo della chrome mentre sopra ne aveva
    // 16 — appoggiato in basso invece che centrato nel suo spazio.
    // `pack-scope` su TUTTA la chrome del Profilo, non solo sulla riga in alto:
    // il contorno del banner legge `--color-brand` (vedi `UserBgShape`), quindi
    // restava l'ultimo pezzo di verde acqua su una schermata di cuoio.
    // Sul Profilo la chrome non poggia più direttamente sulle scaglie: diventa
    // una TARGA di cuoio, angoli inferiori stondati come la barra in basso. Le
    // due si guardano da capo a piede dello schermo e chiudono la schermata fra
    // due pezzi dello stesso materiale — in mezzo resta lo sfondo, che così
    // legge come il dentro dello zaino invece che come il fondo di tutto.
    //
    // La cucitura è la stessa della barra: un anello interno, non un `border`,
    // così non aggiunge un pixel all'ingombro né taglia gli angoli stondati.
    <div
      className={cn(
        'relative z-10 shrink-0 px-4 pt-3 transition-[padding] duration-300 ease-out',
        onProfile
          ? 'rounded-b-2xl bg-pack pb-4 pack-scope [box-shadow:inset_0_0_0_1px_var(--color-pack-stitch)]'
          : 'pb-2',
      )}
    >
      {/* La riga in alto (le valute) resta SEMPRE a tutta larghezza, estesa o
          compressa. Prima, da compressi, diventava la colonna a destra del
          banner: il portafoglio si rimpiccioliva e si spostava proprio mentre
          l'occhio stava seguendo lo scroll. Ora quella colonna ha un canale suo
          (`sideNode`) e ospita il tasto della sezione attiva. */}
      {/* Livello + valute sono CHROME, non contenuto della Hub: stanno qui e
          quindi compaiono su ogni schermata. Prima li montava la Hub attraverso
          il portale, e sparivano appena si entrava in una sottosezione — il
          saldo si vedeva solo dove non serviva spenderlo. */}
      <div className="relative z-[2] flex min-h-8 items-center gap-1.5">
        {/* Livello e nickname SPARISCONO sul Profilo, e le valute si prendono
            la riga intera.
            Non è pulizia: sul Profilo quelle due cose ci sono già, più grandi e
            al posto giusto — il nickname sotto il banner, il livello alle
            estremità della barra dell'esperienza. Ripeterle in alto le avrebbe
            mostrate due volte a due dimensioni diverse nella stessa schermata.

            SI RESTRINGE, non si smonta: la larghezza massima va a zero e il
            contenuto svanisce insieme, così arrivando da un'altra sezione le
            valute si allargano invece di saltare. Smontandolo il salto sarebbe
            stato secco, e chi arriva qui avrebbe visto la riga «rompersi».
            `-mr-1.5` mangia il proprio `gap` mentre chiude, altrimenti a
            larghezza zero resterebbe comunque un vuoto di sei pixel. */}
        <div
          aria-hidden={onProfile}
          className={cn(
            'flex min-w-0 items-center gap-1.5 overflow-hidden',
            'transition-[max-width,opacity,margin] duration-300 ease-out',
            onProfile ? 'pointer-events-none -mr-1.5 max-w-0 opacity-0' : 'max-w-[70%] opacity-100',
          )}
        >
          <LevelBadge />

          {/* Il nickname QLASH, fra il livello e il portafoglio.
              SOLO `username`, mai nome e cognome e mai l'email come ripiego. Il
              nome legale non è un'identità di gioco, ed è dato personale; l'email
              lo è ancora di più — e questa riga è chrome, cioè sta su OGNI
              schermata, anche mentre qualcuno guarda lo schermo da sopra la
              spalla. Chi non ha ancora scelto un nome non vede nulla: uno spazio
              vuoto dice meno di un indirizzo di posta.
              `truncate`: un nickname lungo si taglia coi puntini invece di
              schiacciare le valute, che sono numeri e diventano illeggibili molto
              prima di un nome.
              Il respiro sta a DESTRA (`pr-3`) e non come `gap` sulla riga: deve
              staccarsi dalle valute, non allontanarsi anche dal livello — quello
              alla sua sinistra è l'altra metà della stessa identità, e separarli
              li farebbe leggere come tre cose invece di due. */}
          {me?.username && (
            <span className="min-w-0 shrink truncate pr-3 text-sm font-bold text-fg">
              {me.username}
            </span>
          )}
        </div>

        <CurrencyHud className="min-w-0 flex-1" />
        {/* Lo slot resta per una rotta che voglia dire la sua in questa riga. */}
        <div ref={setTopNode} className="flex shrink-0 items-center" />
      </div>

      {/* Il margine superiore sta sulla RIGA, non sul banner: applicato al
          banner entrava nel suo box e `items-center` centrava quel box
          margine incluso, spingendo il banner in basso e lasciando il tasto
          di sezione piu' in alto. Sulla riga, i due partono dalla stessa
          quota e si centrano fra loro. */}
      <div className={cn(hasSide ? 'mt-3 flex items-center gap-2' : 'contents')}>
        {/* Il banner ESCE dal bordo sinistro: -ml-8 supera il px-4 del
            contenitore, quindi il suo lato sinistro finisce fuori schermo e il
            bordo verde acqua non si vede. Cosi' lo stendardo sembra entrare da
            fuori invece di essere una scheda appoggiata al centro — e la punta
            a destra resta l'unico terminale visibile, che e' il verso giusto
            per una forma a bandiera. Lo shell ha overflow-hidden, quindi la
            parte in eccesso e' tagliata, non genera scorrimento. */}
        <div
          className={cn(
            // Da compressi il margine e' della riga (sopra), non di questo
            // elemento: qui spingerebbe giu' solo il banner.
            'relative -ml-8 transition-all duration-300 ease-out',
            compact ? 'w-[62%] shrink-0' : onProfile ? 'w-[90%] shrink-0' : 'mt-4',
          )}
        >
          {/* No name printed here, deliberately. It used to read `me.firstName` —
              the player's LEGAL name — which is personal data and not an identity
              in a game. A player is referred to by their QLASH account or their
              in-game account, both of which live in Settings. */}
          <UserHeaderBanner
            userId={me?.id}
            onPress={() => navigate('/profile')}
            ariaLabel={t('profile', { defaultValue: 'Profile' })}
          />

          {/* The player's QAvatar — their official picture, every screen. */}
          <BannerAvatar />

          {/* Route overlay target. Sits last so it stacks above, and is
              click-through so the banner keeps its own tap. */}
          <div ref={setOverlayNode} className="pointer-events-none absolute inset-0 z-[1]" />
        </div>

        {/* La colonna a destra del banner. Da compressi ospita il tasto della
            sezione attiva (Hub); sul Profilo il tasto di modifica del banner.
            A banner esteso e fuori dal Profilo non esiste: la griglia sarebbe
            visibile e direbbe la stessa cosa due volte. */}
        {hasSide && (
          <div ref={setSideNode} className="flex min-w-0 flex-1 items-center justify-end" />
        )}
      </div>

      {/* La riga sotto il banner: l'identità del giocatore (nickname + barra
          dell'esperienza sul Profilo).
          `mt-6` e non `mt-3`: l'avatar del giocatore SPORGE sotto il bordo
          inferiore del banner (vedi `BannerAvatar`, `bottom-[-25%]`), e con lo
          stacco normale la testa gli sarebbe finita addosso. */}
      <div ref={setBottomNode} className="mt-6 empty:mt-0" />

      {/* Il tasto per comprimere/espandere non c'e' piu': la compressione e'
          una CONSEGUENZA dello scroll, non una preferenza. Un comando manuale
          che fa la stessa cosa del gesto aggiunge un controllo da capire e uno
          stato che puo' contraddirlo (banner espanso a mano mentre si scorre). */}
    </div>
  );
}
